/**
 * Playground interceptor handlers for Game Management CRUD operations.
 * Matches API pattern: /v1/web/game-mgmt
 */
import { PlaygroundSessionStore, PlaygroundLimitError } from '../PlaygroundSessionStore.js';

const store = new PlaygroundSessionStore('pg_games', 5);
const secretsStore = new PlaygroundSessionStore('pg_game_secrets', 50);
const now = () => new Date().toISOString();

function generateMockSecret() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let secret = '';
    for (let i = 0; i < 48; i++) {
        secret += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return secret;
}

function generateMockKeyId() {
    return 'key-' + Math.random().toString(36).substring(2, 10);
}

/**
 * GET /v1/web/game-mgmt - list all games.
 * Returns { games: [...] } to match the real API response shape.
 */
export function handleGetGames(config, match) {
    return { games: store.getAll() };
}

/**
 * GET /v1/web/game-mgmt/orgId/:orgId - filter games by orgId.
 * Returns { games: [...] } to match the real API response shape.
 */
export function handleGetGamesByOrgId(config, match) {
    const orgId = match[1];
    const games = store.getAll().filter(game => game.orgId === orgId);
    return { games };
}

/**
 * GET /v1/web/game-mgmt/id/:id - single game by id.
 * Returns { game: {...} } to match the real API response shape.
 */
export function handleGetGameById(config, match) {
    const id = match[1];
    const item = store.getById(id);
    if (!item) {
        throw new Error('Game with id ' + id + ' not found');
    }
    return { game: item };
}

/**
 * GET /v1/web/game-mgmt/game-client-id/:gameClientId - single game by gameClientId.
 * Returns { game: {...} } to match the real API response shape.
 */
export function handleGetGameByClientId(config, match) {
    const gameClientId = match[1];
    const allGames = store.getAll();
    const item = allGames.find(g => g.gameClientId === gameClientId) || store.getById(gameClientId);
    if (!item) {
        throw new Error('Game with gameClientId ' + gameClientId + ' not found');
    }
    return { game: item };
}

/**
 * POST /v1/web/game-mgmt - create new game.
 */
export function handleCreateGame(config, match) {
    const data = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
    const id = PlaygroundSessionStore.getNextId();
    const newItem = {
        ...data,
        id,
        gameClientId: id,
        state: data.state || 'ACTIVE',
        lastModifiedDate: now(),
        lastModifiedBy: 'Playground Explorer',
    };
    return store.create(newItem);
}

/**
 * PUT /v1/web/game-mgmt/:id - update existing game.
 */
export function handleUpdateGame(config, match) {
    const id = match[1];
    const data = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
    const updated = {
        ...data,
        id,
        lastModifiedDate: now(),
        lastModifiedBy: 'Playground Explorer',
    };
    return store.update(id, updated);
}

/**
 * DELETE /v1/web/game-mgmt/:id - remove game.
 */
export function handleDeleteGame(config, match) {
    const id = match[1];
    store.delete(id);
    return { success: true };
}

/**
 * POST /v1/web/game-mgmt/:id/suspend - update game state to SUSPENDED.
 */
export function handleSuspendGame(config, match) {
    const id = match[1];
    const existing = store.getById(id);
    if (!existing) {
        throw new Error('Game with id ' + id + ' not found');
    }
    return store.update(id, {
        ...existing,
        state: 'SUSPENDED',
        lastModifiedDate: now(),
        lastModifiedBy: 'Playground Explorer',
    });
}

/**
 * POST /v1/web/game-mgmt/:id/activate - update game state to ACTIVE.
 */
export function handleActivateGame(config, match) {
    const id = match[1];
    const existing = store.getById(id);
    if (!existing) {
        throw new Error('Game with id ' + id + ' not found');
    }
    return store.update(id, {
        ...existing,
        state: 'ACTIVE',
        lastModifiedDate: now(),
        lastModifiedBy: 'Playground Explorer',
    });
}

/**
 * POST /v1/web/game-mgmt/:id/sunset - update game state to SUNSET.
 */
export function handleSunsetGame(config, match) {
    const id = match[1];
    const existing = store.getById(id);
    if (!existing) {
        throw new Error('Game with id ' + id + ' not found');
    }
    return store.update(id, {
        ...existing,
        state: 'SUNSET',
        lastModifiedDate: now(),
        lastModifiedBy: 'Playground Explorer',
    });
}

// ── Secret Management ──────────────────────────────────────────────

/**
 * GET /v1/web/game-mgmt/:gameRegistryId/:gameClientId/secrets
 * Returns mock secret key metadata (without actual secret values).
 */
export function handleGetSecretKeys(config, match) {
    const gameClientId = match[2];
    const allSecrets = secretsStore.getAll().filter(s => s.gameClientId === gameClientId);
    return {
        gameClientId,
        keys: allSecrets.map(s => ({
            keyId: s.keyId,
            lastModifiedBy: s.lastModifiedBy,
            lastModifiedDate: s.lastModifiedDate,
        })),
    };
}

/**
 * POST /v1/web/game-mgmt/:gameRegistryId/:gameClientId/rotate-secret
 * Generates a new mock secret and stores it.
 */
export function handleRotateSecret(config, match) {
    const gameClientId = match[2];
    const keyId = generateMockKeyId();
    const secret = generateMockSecret();
    const timestamp = now();

    const newSecret = {
        id: keyId,
        gameClientId,
        keyId,
        secret,
        lastModifiedBy: 'Playground Explorer',
        lastModifiedDate: timestamp,
    };
    secretsStore.create(newSecret);

    return {
        gameClientId,
        keyId,
        secret,
        lastModifiedBy: 'Playground Explorer',
        lastModifiedDate: timestamp,
    };
}

/**
 * DELETE /v1/web/game-mgmt/:gameRegistryId/:gameClientId/secrets/:keyId
 * Removes a mock secret by keyId.
 */
export function handleRevokeSecret(config, match) {
    const keyId = match[3];
    const allSecrets = secretsStore.getAll();
    const target = allSecrets.find(s => s.keyId === keyId);
    if (target) {
        secretsStore.delete(target.id);
    }
    return { success: true };
}
